import { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import { Building2, Users, Clock, TrendingUp, TrendingDown, CalendarDays, BarChart3 } from 'lucide-react';

function formatSigned(v) {
  return `${v >= 0 ? '+' : ''}${v}h`;
}

function CompanyCard({ name, employeeCount, activeThisWeek, totalHoursWeek, totalContractHours, weekDiff, totalOvertimeBalance, shiftCount }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
            <Building2 className="w-4 h-4 text-blue-600" strokeWidth={1.5} />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900">{name}</h3>
            <p className="text-[11px] text-slate-400">
              {employeeCount} employé(s) | {activeThisWeek} actif(s) cette semaine
            </p>
          </div>
        </div>
        <div className={`text-sm font-bold font-data px-2 py-1 rounded ${weekDiff >= 0 ? 'text-emerald-700 bg-emerald-50' : 'text-rose-700 bg-rose-50'}`}>
          {formatSigned(weekDiff)}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2">
        <div className="bg-slate-50 rounded-lg p-2 text-center">
          <div className="text-base font-bold font-data text-slate-900">{totalHoursWeek}h</div>
          <div className="text-[9px] text-slate-400 uppercase tracking-wider">Effectuées</div>
        </div>
        <div className="bg-slate-50 rounded-lg p-2 text-center">
          <div className="text-base font-bold font-data text-slate-500">{totalContractHours}h</div>
          <div className="text-[9px] text-slate-400 uppercase tracking-wider">Contrat</div>
        </div>
        <div className="bg-slate-50 rounded-lg p-2 text-center">
          <div className={`text-base font-bold font-data ${totalOvertimeBalance >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
            {formatSigned(totalOvertimeBalance)}
          </div>
          <div className="text-[9px] text-slate-400 uppercase tracking-wider">H.Supp</div>
        </div>
        <div className="bg-slate-50 rounded-lg p-2 text-center">
          <div className="text-base font-bold font-data text-slate-700">{shiftCount}</div>
          <div className="text-[9px] text-slate-400 uppercase tracking-wider">Shifts</div>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboardDialog({ open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) fetchDashboard();
  }, [open]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.dashboard();
      setData(res.data);
    } catch { /* */ }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="admin-dashboard-dialog" className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" strokeWidth={1.5} />
            Vue d'ensemble
          </DialogTitle>
          <DialogDescription>
            {data ? `Semaine du ${new Date(data.week_start).toLocaleDateString('fr-FR')} au ${new Date(data.week_end).toLocaleDateString('fr-FR')}` : 'Chargement...'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-slate-400 text-sm">Chargement...</div>
        ) : data ? (
          <div className="space-y-4 py-2">
            {/* Global summary */}
            <div className="bg-slate-900 text-white rounded-xl p-5">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold font-data">{data.global.total_employees}</div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">Employés</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold font-data">{data.global.total_hours_week}h</div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">Heures semaine</div>
                </div>
                <div className="text-center">
                  <div className={`text-2xl font-bold font-data ${data.global.week_diff >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {formatSigned(data.global.week_diff)}
                  </div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">Diff. semaine</div>
                </div>
                <div className="text-center">
                  <div className={`text-2xl font-bold font-data ${data.global.total_overtime_balance >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {formatSigned(data.global.total_overtime_balance)}
                  </div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">H.Supp total</div>
                </div>
              </div>
            </div>

            {/* Per-company cards */}
            {data.companies.map(c => (
              <CompanyCard
                key={c.company.id}
                name={c.company.name}
                employeeCount={c.employee_count}
                activeThisWeek={c.active_this_week}
                totalHoursWeek={c.total_hours_week}
                totalContractHours={c.total_contract_hours}
                weekDiff={c.week_diff}
                totalOvertimeBalance={c.total_overtime_balance}
                shiftCount={c.shift_count}
              />
            ))}

            {/* Unaffiliated */}
            {data.unaffiliated.employee_count > 0 && (
              <CompanyCard
                name="Sans société"
                employeeCount={data.unaffiliated.employee_count}
                activeThisWeek={0}
                totalHoursWeek={data.unaffiliated.total_hours_week}
                totalContractHours={data.unaffiliated.total_contract_hours}
                weekDiff={data.unaffiliated.week_diff}
                totalOvertimeBalance={data.unaffiliated.total_overtime_balance}
                shiftCount={data.unaffiliated.shift_count}
              />
            )}
          </div>
        ) : (
          <div className="py-8 text-center text-slate-400 text-sm">Aucune donnée</div>
        )}
      </DialogContent>
    </Dialog>
  );
}
